import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Trash2, GripVertical, ShoppingCart } from 'lucide-react';
import PriceChange from './PriceChange';
import { useRealtimePrice } from '../hooks/useRealtimePrice';
import { useActivityTracker } from '../hooks/useActivityTracker';

interface WatchlistRowProps {
  symbol: string;
  name: string;
  staticPrice: number;
  staticChange: number;
  previousClose?: number;
  alertsSet: number;
  currency: string;
  onDelete: () => void;
  onSetAlert: () => void;
  onBuy?: (symbol: string, price: number) => void;
  draggable?: boolean;
  onDragStart?: () => void;
  onDragOver?: () => void;
  onDrop?: () => void;
  isDragging?: boolean;
  isDragOver?: boolean;
}

const WatchlistRow: React.FC<WatchlistRowProps> = ({
  symbol,
  name,
  staticPrice,
  staticChange,
  previousClose,
  alertsSet,
  currency,
  onDelete,
  onSetAlert,
  onBuy,
  draggable = false,
  onDragStart,
  onDragOver,
  onDrop,
  isDragging = false,
  isDragOver = false,
}) => {
  const navigate = useNavigate();
  const isStreaming = symbol.endsWith('.FOREX') || symbol.endsWith('.CC');
  const livePrice = useRealtimePrice(isStreaming ? symbol : '');
  const { trackEvent } = useActivityTracker();

  const handleSymbolClick = () => {
    trackEvent('company_click', symbol, { source_page: 'watchlist' });
    navigate(`/asset/${symbol}`);
  };

  // Use live price if available, otherwise fall back to static price
  const displayPrice = livePrice.isLive && livePrice.price ? livePrice.price : staticPrice;

  // Calculate change percentage from previous close for accuracy
  const displayChange = livePrice.isLive && livePrice.price && previousClose
    ? ((livePrice.price - previousClose) / previousClose) * 100
    : staticChange;

  return (
    <tr
      className={`hover:bg-slate-700/30 transition-colors group cursor-move ${
        isDragging ? 'opacity-50' : ''
      } ${isDragOver ? 'border-t-2 border-teal-500' : ''}`}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <td className="px-4 py-3">
        <GripVertical size={16} className="text-slate-600 group-hover:text-slate-400" />
      </td>
      <td
        className="px-4 py-3 cursor-pointer"
        onClick={handleSymbolClick}
      >
        <div className="flex items-center gap-2">
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium">{name}</p>
              {livePrice.isLive && (
                <span className="flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500/10 border border-emerald-500/30 rounded text-[10px] font-semibold text-emerald-400 uppercase tracking-wide">
                  <span className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse" />
                  LIVE
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500">{symbol}</p>
          </div>
        </div>
      </td>
      <td
        className="px-4 py-3 text-right font-mono cursor-pointer"
        onClick={handleSymbolClick}
      >
        {currency}
        {displayPrice.toFixed(2)}
      </td>
      <td
        className="px-4 py-3 cursor-pointer"
        onClick={handleSymbolClick}
      >
        <div className="flex justify-end">
          <PriceChange value={displayChange} />
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="text-xs bg-slate-700/50 px-2 py-1 rounded">{alertsSet}</span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          {onBuy && (
            <button
              onClick={() => onBuy(symbol, displayPrice)}
              className="flex items-center gap-1 px-2.5 py-1.5 bg-green-600 hover:bg-green-500 text-white text-xs font-semibold rounded transition-colors"
              title="Buy"
            >
              <ShoppingCart size={13} />
              Buy
            </button>
          )}
          <button
            onClick={onSetAlert}
            className="p-1.5 hover:bg-teal-500/20 text-teal-400 rounded transition-colors"
            title="Set price alert"
          >
            <Bell size={16} />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 hover:bg-red-500/20 text-red-400 rounded transition-colors"
            title="Remove from watchlist"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </td>
    </tr>
  );
};

export default WatchlistRow;
